import type { FolderScopeType, Routes } from '@drifti-web/doc-browser-context';

type ID = string | number;

const trailingSlashRegex = /\/$/;

const r = {
  root: (
    path:
      | 'home'
      | 'projects'
      | 'crm'
      | 'offers'
      | 'invoices'
      | 'settings'
      | 'time-material'
      | 'account'
      | 'settings'
      | 'addons'
      | 'key-figures'
      | 'notifications',
    subPath?: string
  ) => ['', path, subPath].join('/').replace(trailingSlashRegex, ''),

  invoices: () => '/invoices',
  notification: (id: ID) => `/notifications/${id}`,
  keyFigures: () => '/key-figures',
  jobs: () => '/projects/jobs',
  jobList: () => `${r.jobs()}/list`,
  jobStatusBoard: () => `${r.jobs()}/board`,
  jobBoard: (boardId: ID) => `${r.jobs()}/boards/${boardId}`,
  job: (jobId: ID) => `${r.jobs()}/${jobId}`,
  offer: (jobId: ID, offerId: ID) => `${r.jobs()}/${jobId}/offers/${offerId}`,
  jobExpenses: (jobId: ID) => `${r.job(jobId)}/expenses`,
  jobDocs: (jobId: ID) => `${r.job(jobId)}/documentation`,
  expenseGroup: (jobId: ID, expenseGroupId: ID) => `${r.jobExpenses(jobId)}/groups/${expenseGroupId}`,
  order: (jobId: ID, orderId: ID, extra?: string) => `${r.jobs()}/${jobId}/orders/${orderId}${extra ?? ''}`,
  invoice: ({ jobId, orderId, id }: { jobId: ID; orderId: ID; id: ID }, extra?: string) =>
    `${r.order(jobId, orderId)}/invoices/${id}${extra ?? ''}`,
  createInvoice: (jobId: ID, orderId: ID) => `${r.order(jobId, orderId)}/invoice`,

  customer: (customerId: ID) => `/crm/customers/${customerId}`,
  customerDocs: (customerId: ID) => `${r.customer(customerId)}/documentation`,
  customerGroup: (groupId: ID) => `/crm/customer-groups/${groupId}`,
  importCustomers: () => '/crm/customers/import',

  bundles: () => '/account/bundles/',
  bundle: (id: ID) => `${r.bundles()}/${id}`,

  jobTemplate: (id: ID) => `/account/templates/jobs/${id}`,
  jobTemplateDocs: (id: ID) => `${r.jobTemplate(id)}/documentation`,
  offerTemplate: (id: ID) => `/account/templates/offers/${id}`,
  offerTemplates: () => '/account/templates/offers',
  productSearch: (query?: string) => `/time-material/products?${query}`,

  productBundles: () => '/account/bundles',
  supplierOrders: () => '/time-material/orders/list',
  supplierOrder: (id: ID) => `/time-material/orders/${id}/dashboard`,
  shoppingCart: (id: ID) => `/time-material/orders/drafts/${id}`,

  editRiskAssessment: (riskAssessmentId: ID) => `/account/documentation/risks/${riskAssessmentId}`,

  accountDocs: () => r.root('account', 'documentation'),
  // Nested under documentation so the "Bedriftsdokumentasjon" menu item stays
  // highlighted while in the trash.
  accountTrash: () => `${r.accountDocs()}/trash`,

  productPackages: {
    index: () => r.root('account', 'packages'),
    edit: (id: ID) => r.root('account', `packages/${id}`),
  },

  deviations: {
    index: () => '/account/deviations',
    new: ({ jobId }: { jobId?: ID | null }) =>
      jobId ? `${r.job(jobId)}/documentation/deviations/new` : `${r.deviations.index()}/new`,
    // A deviation opens where it belongs — inside its job when it has one, under
    // documentation otherwise — so the surrounding menu item stays highlighted
    // either way. That puts `edit` on a different path than `index`, which sits
    // outside documentation. A `/deviations/:id` subroute would let the two line
    // up, but there is none.
    edit: ({ id, jobId }: { id: ID; jobId?: ID | null }) =>
      jobId ? `${r.job(jobId)}/documentation/deviations/${id}` : `/account/documentation/deviations/${id}`,
  },

  inventories: {
    index: () => '/time-material/inventories',
    show: (id: ID) => `/time-material/inventories/${id}`,
    receiving: ({ inventoryId, supplierOrderId }: { inventoryId?: ID; supplierOrderId?: ID }) => {
      const search = new URLSearchParams();
      if (inventoryId) search.append('inventoryId', String(inventoryId));
      if (supplierOrderId) search.append('supplierOrderId', String(supplierOrderId));
      const query = search.toString();
      const path = '/time-material/inventories/receiving';
      return query ? `${path}?${query}` : path;
    },
    count: (id?: ID) => (id ? `/time-material/inventories/counts/${id}` : '/time-material/inventories/all/counts'),
  },
} as const;

type RouteFn = (...args: any[]) => string;
type RouteObject = {
  [_x in string]: RouteFn | RouteObject;
};

// Assign r to "routesTypeCheck" which has type of RouteObject. This ensures that r has shape of RouteObject.
// I found no way of both having dynamic shape of r + make it confirm to a RouteObject without this little detoured
const routesTypeCheck: RouteObject = r;
export const routes = routesTypeCheck as typeof r;

// Absolute paths starting from the scope's `documentation` route prefix
// (e.g. /account/documentation, /projects/jobs/:jobId/documentation,
// /crm/customers/:customerId/documentation). Each Root computes its own
// docRoot and passes it in so that navigation works correctly from any
// depth — including subfolders, where a relative `folders/X` would otherwise
// resolve against the current URL and produce `folders/current/folders/X`.
// Sub-routes are mounted in components/AppRouter/routes.tsx.
// Doc-browser root path for a folder's scope (where an alias's original lives).
// system has no navigable view, so it falls back to plain text.
const docRootForScope = (type: FolderScopeType, ownerId: string): string | undefined => {
  switch (type) {
    case 'accountDocs':
      return r.accountDocs();
    case 'trash':
      return r.accountTrash();
    case 'job':
      return r.jobDocs(ownerId);
    case 'customer':
      return r.customerDocs(ownerId);
    case 'template':
      return r.jobTemplateDocs(ownerId);
    default:
      return undefined;
  }
};

export const docBrowserRoutes = (root: string): Routes => ({
  folder: (id) => `${root}/folders/${id}`,
  folderRoot: () => root,
  // URL to a folder in another scope (an alias's "Lagret i" → where the original
  // lives). Independent of `root`: it targets the scope the original sits in.
  crossScopeFolder: (scope, folderId) => {
    const docRoot = docRootForScope(scope.type, scope.ownerId);
    return docRoot === undefined ? undefined : `${docRoot}/folders/${folderId}`;
  },
  document: (id) => `${root}/docs/${id}`,
  newDocument: (id) => `${root}/docs/${id}?isNew=true`,
  deviation: (id) => `${root}/deviations/${id}`,
  newDeviation: () => `${root}/deviations/new`,
  riskAssessment: (id) => `${root}/risks/${id}`,
  editRiskAssessment: (id) => `${root}/risks/${id}`,
  formTemplate: (id) => `${root}/form-templates/${id}`,
  formSubmission: (id) => `${root}/forms/${id}`,
});
